import {
  doc,
  onSnapshot,
  runTransaction,
  getDoc,
  setDoc,
  Unsubscribe,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { User } from "../types/user";

// The saved-users directory lives in Firestore at users/{uid}, keyed by the
// signed-in (anonymous) account, in a `directory: User[]` field.
const LEGACY_KEY = "checkmates.knownPeople";

const norm = (s?: string) => s?.trim().toLowerCase() ?? "";

// Two people are the same person when both their name and venmo match. Used
// only to reuse an existing id instead of minting a duplicate — the directory
// itself is deduped by id.
export function sameNameVenmo(
  a: { name: string; venmo?: string },
  b: { name: string; venmo?: string }
): boolean {
  return norm(a.name) === norm(b.name) && norm(a.venmo) === norm(b.venmo);
}

// Firestore rejects `undefined`, so only include venmo when it has a value.
function clean(user: User): User {
  const name = user.name.trim();
  const venmo = user.venmo?.trim();
  return venmo ? { id: user.id, name, venmo } : { id: user.id, name };
}

function directoryRef(uid: string) {
  return doc(db, "users", uid);
}

// Live directory for a user; the callback fires on every change.
export function subscribeDirectory(
  uid: string,
  cb: (people: User[]) => void
): Unsubscribe {
  return onSnapshot(directoryRef(uid), (snap) => {
    cb((snap.data()?.directory as User[]) ?? []);
  });
}

// Add or update a person in the directory, deduped by id. Uses a transaction so
// concurrent writes (e.g. two tabs) don't clobber each other.
export async function rememberPerson(uid: string, user: User): Promise<void> {
  if (user.name.trim() === "") return;
  const entry = clean(user);
  const ref = directoryRef(uid);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const list = ((snap.data()?.directory as User[]) ?? []).slice();
    const idx = list.findIndex((p) => p.id === entry.id);
    if (idx === -1) list.push(entry);
    else list[idx] = entry;
    tx.set(ref, { directory: list }, { merge: true });
  });
}

export async function forgetPerson(uid: string, id: string): Promise<void> {
  const ref = directoryRef(uid);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const list = (snap.data()?.directory as User[]) ?? [];
    tx.set(ref, { directory: list.filter((p) => p.id !== id) }, { merge: true });
  });
}

// Look up a remembered person by name + venmo within an already-loaded list, so
// adding them reuses their existing id rather than creating a duplicate.
export function findKnownPerson(
  people: User[],
  name: string,
  venmo?: string
): User | undefined {
  return people.find((p) => sameNameVenmo(p, { name, venmo }));
}

// One-time move of any pre-existing localStorage directory into Firestore.
export async function migrateLocalDirectory(uid: string): Promise<void> {
  let legacy: User[] = [];
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    legacy = Array.isArray(parsed) ? parsed : [];
  } catch {
    return;
  }

  if (legacy.length > 0) {
    const ref = directoryRef(uid);
    const snap = await getDoc(ref);
    const existing = (snap.data()?.directory as User[]) ?? [];
    // only seed when the cloud directory is still empty, to avoid overwriting
    if (existing.length === 0) {
      await setDoc(ref, { directory: legacy.map(clean) }, { merge: true });
    }
  }
  localStorage.removeItem(LEGACY_KEY);
}
